import { useEffect, useRef, type RefObject } from 'react'
import { afterTransition } from '../../hooks/gestures/afterTransition'
import { haptic } from '../../hooks/gestures/haptic'
import { EDGE_GUARD, REPLY_MARK, REPLY_MAX, type SwipeOptions } from '../../hooks/gestures/recognisers'

/**
 * Swipe to reply (spec §3.2), for usePointerGestures: the message follows the finger while the reply arrow fades
 * in and fills at the mark, then springs back; letting go past the mark calls onReply. Null: the message does not
 * move. The row is styled directly (data-swipe and custom properties, which React leaves alone when it re-renders
 * the message), so a swipe does not re-render it.
 */
export function useSwipeToReply(rowRef: RefObject<HTMLElement | null>, onReply: (() => void) | null): SwipeOptions | null {
  const settle = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(settle.current), [])
  if (!onReply) return null

  return {
    direction: 'right',
    max: REPLY_MAX,
    mark: REPLY_MARK,
    // Swipes from the screen edge go back to the conversation list.
    startX: { min: EDGE_GUARD },
    onStart: () => {
      clearTimeout(settle.current)
      if (rowRef.current) rowRef.current.dataset.swipe = 'moving'
    },
    onMove: ({ offset, reached }) => {
      const row = rowRef.current
      if (!row) return
      row.style.setProperty('--swipe', `${offset}px`)
      row.style.setProperty('--swipe-progress', String(Math.min(1, offset / REPLY_MARK)))
      const state = reached ? 'reached' : 'moving'
      if (row.dataset.swipe === state) return
      row.dataset.swipe = state
      if (reached) haptic()
    },
    onEnd: ({ committed }) => {
      const row = rowRef.current
      if (row) {
        row.dataset.swipe = 'returning'
        row.style.setProperty('--swipe', '0px')
        row.style.setProperty('--swipe-progress', '0')
        settle.current = afterTransition(row, () => {
          delete row.dataset.swipe
          row.style.removeProperty('--swipe')
          row.style.removeProperty('--swipe-progress')
        })
      }
      if (committed) onReply()
    },
  }
}
